import React, { useState, useEffect, useMemo } from 'react';
import {
  StyleSheet,
  Text,
  View,
  TextInput,
  TouchableOpacity,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { AntDesign, Ionicons } from '@expo/vector-icons';
import Checkbox from 'expo-checkbox';
import DataTable from "react-data-table-component";
import Modal from "react-native-modal";
import { WorkState, useScale, ScaleFn, TablePagination , useDarkMode} from './common';
import Data from './Data';
import { getStorage, setStorage } from '../storage';

// ─── 테마 토큰 ────────────────────────────────────────
const DARK = {
  bg:        '#0F172A',
  card:      '#1E293B',
  cardInner: '#0F172A',
  border:    'rgba(99,179,237,0.12)',
  text:      '#E2E8F0',
  textSub:   '#94A3B8',
  muted:     '#a8adb497',
  trackBg:   'rgba(255,255,255,0.05)',
};
const LIGHT = {
  bg:        '#F9FAFB',
  card:      '#FFFFFF',
  cardInner: '#F3F4F6',
  border:    '#E5E7EB',
  text:      '#111827',
  textSub:   '#4B5563',
  muted:     '#9CA3AF',
  trackBg:   'rgba(0,0,0,0.03)',
};
type Theme = typeof DARK;

const S = {
  accent:  '#38BDF8',
  ok:      '#34D399',
  err:     '#F87171',
  warn:    '#FB923C',
  replace: '#FBBF24',
  purple:  '#A78BFA',
  pink:    '#F472B6',
};

// 반응형 스케일 훅(useScale)/ScaleFn은 ./common 공통 모듈에서 import (전 화면 공통 패턴)

// ─── RegisterModal ────────────────────────────────────
const RegisterModal = ({ visible, setVisible, groupList, getList, T }: any) => {
  const { scale, s } = useScale();
  const styles = useMemo(() => makeStyles(s), [scale]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);

  const toggleArea = (area: string) => {
    setSelectedAreas(prev =>
      prev.includes(area) ? prev.filter(a => a !== area) : [...prev, area]
    );
  };

  const toggleAll = () => {
    if (selectedAreas.length === groupList.length) {
      setSelectedAreas([]);
    } else {
      setSelectedAreas(groupList.map((g: any) => g.AREA));
    }
  };

  const onRegister = () => {
    if (!name.trim()) { alert('이름을 입력해주세요.'); return; }
    if (!phone.trim()) { alert('전화번호를 입력해주세요.'); return; }
    if (selectedAreas.length === 0) { alert('구역을 선택해주세요.'); return; }

    Data.fetchData('user/initialRegistrationOfCaregiver', {
      NAME: name.trim(),
      PHONE: phone.trim(),
      AREAS: selectedAreas,
    }, (result: any, count: any) => {
      if (count > 0) {
        alert(count + '개 구역에 등록되었습니다.');
        setName('');
        setPhone('');
        setSelectedAreas([]);
        setVisible(false);
        getList();
      } else if (count === -2) {
        alert('이미 등록된 전화번호입니다.');
      } else {
        alert('등록에 실패하였습니다.');
      }
    }, true);
  };

  const onClose = () => {
    setName('');
    setPhone('');
    setSelectedAreas([]);
    setVisible(false);
  };

  return (
    <Modal
      style={{ flex: 1 }}
      backdropColor="black"
      isVisible={visible}
      backdropOpacity={0.7}
      onBackdropPress={onClose}
    >
      <View style={[styles.registerModal, { backgroundColor: T.card, borderColor: T.border }]}>
        {/* 헤더 */}
        <View style={[styles.registerHeader, { borderColor: T.border }]}>
          <Text style={{ fontSize: s(20), fontWeight: '700', color: T.text }}>보호사 등록</Text>
          <TouchableOpacity onPress={onClose}>
            <AntDesign name="close" size={s(24)} color={T.textSub} />
          </TouchableOpacity>
        </View>

        {/* 이름 */}
        <View style={styles.registerRow}>
          <Text style={[styles.registerLabel, { color: T.text }]}>이름</Text>
          <View style={[styles.registerInput, { borderColor: T.border, backgroundColor: T.cardInner }]}>
            <TextInput
              style={{ flex: 1, fontSize: s(15), color: T.text, outlineStyle: 'none' } as any}
              placeholder="이름을 입력하세요"
              placeholderTextColor={T.muted}
              autoCapitalize="none"
              value={name}
              onChangeText={setName}
            />
          </View>
        </View>

        {/* 전화번호 */}
        <View style={styles.registerRow}>
          <Text style={[styles.registerLabel, { color: T.text }]}>전화번호</Text>
          <View style={[styles.registerInput, { borderColor: T.border, backgroundColor: T.cardInner }]}>
            <TextInput
              style={{ flex: 1, fontSize: s(15), color: T.text, outlineStyle: 'none' } as any}
              placeholder="전화번호를 입력하세요"
              placeholderTextColor={T.muted}
              autoCapitalize="none"
              keyboardType="phone-pad"
              value={phone}
              onChangeText={setPhone}
            />
          </View>
        </View>

        {/* 구역 선택 */}
        <View style={{ marginTop: s(12) }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: s(8) }}>
            <Text style={[styles.registerLabel, { color: T.text }]}>담당 구역</Text>
            <TouchableOpacity
              onPress={toggleAll}
              style={{ flexDirection: 'row', alignItems: 'center', gap: s(4) }}
            >
              <Checkbox
                style={{ width: s(16), height: s(16) }}
                color={S.accent}
                value={groupList.length > 0 && selectedAreas.length === groupList.length}
                onValueChange={toggleAll}
              />
              <Text style={{ fontSize: s(12), color: T.textSub }}>전체선택</Text>
            </TouchableOpacity>
          </View>
          <View style={[styles.areaGrid, { borderColor: T.border, backgroundColor: T.cardInner }]}>
            {groupList.length === 0 ? (
              <Text style={{ color: T.muted, fontSize: s(14), padding: s(10) }}>등록된 구역이 없습니다.</Text>
            ) : (
              groupList.map((g: any) => {
                const checked = selectedAreas.includes(g.AREA);
                return (
                  <TouchableOpacity
                    key={g.AREA}
                    style={[styles.areaChip, {
                      backgroundColor: checked ? S.accent + '18' : 'transparent',
                      borderColor: checked ? S.accent : T.border,
                    }]}
                    onPress={() => toggleArea(g.AREA)}
                  >
                    <Checkbox
                      style={{ width: s(16), height: s(16), marginRight: s(6) }}
                      color={S.accent}
                      value={checked}
                      onValueChange={() => toggleArea(g.AREA)}
                    />
                    <Text style={{ fontSize: s(14), color: checked ? S.accent : T.text, fontWeight: checked ? '600' : '400' }}>
                      {g.AREA}
                    </Text>
                  </TouchableOpacity>
                );
              })
            )}
          </View>
          {selectedAreas.length > 0 && (
            <Text style={{ fontSize: s(12), color: S.accent, marginTop: s(6) }}>
              {selectedAreas.length}개 구역 선택됨
            </Text>
          )}
        </View>

        {/* 버튼 */}
        <View style={{ flexDirection: 'row', justifyContent: 'center', gap: s(12), marginTop: s(24) }}>
          <TouchableOpacity
            style={[styles.registerBtn, { backgroundColor: S.accent }]}
            onPress={onRegister}
          >
            <Text style={{ fontSize: s(15), fontWeight: '600', color: '#fff' }}>등록</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.registerBtn, { backgroundColor: T.textSub }]}
            onPress={onClose}
          >
            <Text style={{ fontSize: s(15), fontWeight: '600', color: '#fff' }}>취소</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

// ─── DeleteModal ──────────────────────────────────────
const DeleteModal = ({ deleteModal, setDeleteModal, checkedItems, setCheckedItems, getList, T }: any) => {
  const { scale, s } = useScale();
  const styles = useMemo(() => makeStyles(s), [scale]);
  const onDelete = () => {
    setDeleteModal(false);
    Data.fetchData('sensor/deleteCaregiver', {
      DATA: checkedItems
    }, (result: any, count: any) => {
      if (count > 0) {
        setCheckedItems([]);
        setDeleteModal(false);
        getList();
        alert('삭제되었습니다.');
      }
    }, true);
  };

  return (
    <Modal
      style={{ flex: 1 }}
      backdropColor="black"
      isVisible={deleteModal}
      backdropOpacity={0.7}
      onBackdropPress={() => { setDeleteModal(false); }}>
      <View style={[styles.deleteModal, { backgroundColor: T.card, borderColor: T.border }]}>
        <View style={{ justifyContent: 'center', alignItems: 'center' }}>
          <Text style={{ fontSize: s(18), fontWeight: '500', color: T.text }}>선택한 보호사를 삭제하시겠습니까?</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginTop: s(30) }}>
          <TouchableOpacity onPress={() => { setDeleteModal(false); }}>
            <Text style={[styles.removeButtonFont, { color: T.textSub }]}>취소</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => { onDelete() }}>
            <Text style={[styles.removeButtonFont, { color: S.err }]}>삭제</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  )
};

// ─── DeleteBox ────────────────────────────────────────
const DeleteBox = ({ setDeleteModal }: any) => {
  const { scale, s } = useScale();
  const styles = useMemo(() => makeStyles(s), [scale]);
  const onPressDelete = () => {
    setDeleteModal(true);
  }

  return (
    <TouchableOpacity style={styles.removeButton} onPress={() => { onPressDelete() }}>
      <Text style={{ fontSize: s(14), fontWeight: '600', color: '#fff' }}>삭제</Text>
    </TouchableOpacity>
  )
};

// ─── OnCheck ──────────────────────────────────────────
const OnCheck = ({ row, checkedItems, setCheckedItems }: any) => {
  const value = checkedItems.filter((el: any) => el?.ID === row?.ID);

  const onChecked = (checked: any, row: any) => {
    if (checked) {
      setCheckedItems([...checkedItems, { ID: row?.ID }]);
    } else {
      setCheckedItems(checkedItems.filter((el: any) => el?.ID !== row?.ID));
    }
  }

  return (
    <Checkbox
      style={{ margin: 3 }}
      color={S.accent}
      value={value?.length > 0}
      onChange={(e: any) => onChecked(e?.target?.checked, row)}
    />
  )
};

// ─── OnAllChecked ─────────────────────────────────────
const OnAllChecked = ({ checkedItems, setCheckedItems, list }: any) => {
  const onChecked = (checked: any) => {
    if (checked) {
      const checkedList: any[] = [];
      list.forEach((el: any) => checkedList.push({ ID: el?.ID }));
      setCheckedItems(checkedList)
    } else {
      setCheckedItems([]);
    }
  }

  return (
    <Checkbox
      style={{ margin: 3 }}
      color={S.accent}
      value={checkedItems?.length > 0 && checkedItems?.length === list?.length}
      onChange={(e: any) => onChecked(e?.target?.checked)}
    />
  )
};

// ─── Main ─────────────────────────────────────────────
export default function Members({ navigation }: any) {
  const isFocused = useIsFocused();
  const { scale, s } = useScale();
  const styles = useMemo(() => makeStyles(s), [scale]);
  const [list, setList] = useState<any[]>([]);
  const [searchData, setSearchData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const _totalPages = Math.max(1, Math.ceil(((searchData) || []).length / perPage));
  const _curPage = Math.min(Math.max(1, page), _totalPages);
  const pageData = ((searchData) || []).slice((_curPage - 1) * perPage, _curPage * perPage);
  const [search, setSearch] = useState('');
  const [deleteModal, setDeleteModal] = useState(false);
  const [registerModal, setRegisterModal] = useState(false);
  const [checkedItems, setCheckedItems] = useState<any>([]);
  const [groupList, setGroupList] = useState<any[]>([]);
  const { dark, setDark } = useDarkMode();
  const [admin, setAdmin] = useState('1');

  useEffect(() => {
    if (!isFocused) return;
    getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
    getStorage('AutoLogin').then((v: any) => { if (v?.admin !== undefined) setAdmin(v.admin); });
  }, [isFocused]);

  const T = dark ? DARK : LIGHT;

  const tableStyles = {
    headRow:    { style: { backgroundColor: T.cardInner, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(40) } },
    headCells:  { style: { color: T.textSub, fontSize: s(12), fontWeight: '600' as any, paddingLeft: s(12), paddingRight: s(12) } },
    rows:       { style: { backgroundColor: T.card, color: T.text, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(44), '&:hover': { backgroundColor: T.cardInner } } },
    cells:      { style: { color: T.text, fontSize: s(13), paddingLeft: s(12), paddingRight: s(12) } },
    pagination: { style: { backgroundColor: T.card, color: T.textSub, borderTopColor: T.border, borderTopWidth: 1, minHeight: s(44) },
      pageButtonsStyle: { color: T.textSub, fill: T.textSub, '&:disabled': { color: T.muted, fill: T.muted }, '&:hover:not(:disabled)': { backgroundColor: T.cardInner } } },
    noData:     { style: { backgroundColor: T.card, color: T.muted, padding: s(30) } },
  };

  const onChangeSearch = (text: any) => setSearch(text);

  const memberColumns = [
    {
      id: 1,
      name: <OnAllChecked checkedItems={checkedItems} setCheckedItems={setCheckedItems} list={list} />,
      cell: (row: any, index: any) => <OnCheck row={row} checkedItems={checkedItems} setCheckedItems={setCheckedItems} />,
      grow: 0
    },
    {
      id: 2,
      name: "이름",
      selector: (row: any) => row.NAME,
    },
    {
      id: 3,
      name: '전화번호',
      selector: (row: any) => row.PHONE,
    },
    {
      id: 4,
      name: '담당 구역',
      selector: (row: any) => row.AREA,
    },
    {
      id: 5,
      name: '근무 표시',
      selector: (row: any) => WorkState(row.WORKING),
    },
  ];

  useEffect(() => {
    if (!isFocused) return;
    getList();
    // 구역 목록 fetch
    Data.fetchData('group/getGroupList', {}, (result: any, count: any) => {
      if (count > 0) {
        setGroupList(result);
      } else {
        setGroupList([]);
      }
    }, true);
  }, [isFocused]);

  const getList = () => {
    Data.fetchData('group/getCaregiverList', {
      AREA: ""
    }, (result: any, count: any) => {
      if (count > 0) {
        setList(result);
        setSearchData(result);
      } else {
        setList([]);
        setSearchData([]);
      }
    }, true);
  };

  useEffect(() => {
    if (search?.length < 1) {
      getList();
    } else {
      const lists = list?.filter((item: any) => item?.NAME.includes(search));
      setSearchData(lists);
    }
  }, [search]);

  return (
    <View style={[styles.container, { backgroundColor: T.bg }]}>
      <View style={[styles.dataTable, { backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
        {/* ─── 헤더 ─── */}
        <View style={styles.TopBox}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
            <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text }}>보호사 관리</Text>
            {searchData?.length > 0 && (
              <View style={{ backgroundColor: S.accent + '18', paddingHorizontal: s(8), paddingVertical: s(2), borderRadius: s(10) }}>
                <Text style={{ fontSize: s(12), fontWeight: '700', color: S.accent }}>{searchData.length}</Text>
              </View>
            )}
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
            <View style={[styles.searchContainer, { backgroundColor: T.cardInner, borderColor: T.border }]}>
              <Ionicons name="search-sharp" size={s(18)} color={T.muted} style={{ marginRight: s(8) }} />
              <TextInput
                style={[styles.searchBox, { color: T.text }]}
                placeholder="이름을 검색하세요"
                placeholderTextColor={T.muted}
                autoCapitalize='none'
                value={search}
                onChangeText={(text: any) => onChangeSearch(text)}
              />
              {search !== '' && (
                <TouchableOpacity onPress={() => setSearch('')} style={{ padding: s(2) }}>
                  <AntDesign name="closecircle" size={s(14)} color={T.muted} />
                </TouchableOpacity>
              )}
            </View>
            <TouchableOpacity
              style={[styles.headerBtn, { backgroundColor: S.accent }]}
              onPress={() => setRegisterModal(true)}
            >
              <Ionicons name="person-add-outline" size={s(15)} color="#fff" />
              <Text style={{ fontSize: s(14), fontWeight: '600', color: '#fff' }}>등록</Text>
            </TouchableOpacity>
            {admin === '0' && <DeleteBox setDeleteModal={setDeleteModal} />}
            <TouchableOpacity
              style={[styles.iconBtn, { backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }]}
              onPress={() => { navigation.navigate('Setting') }}
            >
              <Ionicons name="settings-sharp" size={s(16)} color={T.textSub} />
            </TouchableOpacity>
          </View>
        </View>
        {/* ─── 테이블 ─── */}
        <DataTable
          defaultSortFieldId={4}
          key={searchData?.idx}
          columns={memberColumns}
          data={pageData}
          customStyles={tableStyles}
          noDataComponent={
            <View style={{ padding: s(40), alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ fontSize: s(16), color: T.muted }}>
                등록된 보호사가 없습니다.
              </Text>
            </View>
          }
          onRowClicked={(row: any) => {
            try { window.localStorage.setItem(`_navParams_MemberDetail_${row.ID}`, JSON.stringify({ data: row })); } catch {}
            navigation.navigate('MemberDetail', { id: String(row.ID) });
          }}
        />
        {(searchData || []).length > 0 && (
          <TablePagination
            rowCount={(searchData || []).length}
            perPage={perPage}
            page={_curPage}
            onChangePage={setPage}
            onChangePerPage={(n) => { setPerPage(n); setPage(1); }}
            T={T}
            s={s}
            accent={S.accent}
          />
        )}
        <DeleteModal
          deleteModal={deleteModal}
          setDeleteModal={setDeleteModal}
          checkedItems={checkedItems}
          setCheckedItems={setCheckedItems}
          getList={getList}
          T={T}
        />
      </View>
      <RegisterModal
        visible={registerModal}
        setVisible={setRegisterModal}
        groupList={groupList}
        getList={getList}
        T={T}
      />
    </View>
  );
}

// ─── 스타일 팩토리 (스케일 s 적용) ────────────────────
const makeStyles = (s: ScaleFn) => StyleSheet.create({
  container: {
    flex: 1,
    padding: s(24),
  },
  dataTable: {
    borderRadius: s(16),
    borderWidth: 0.5,
    overflow: 'hidden',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: s(20),
    elevation: 3,
    paddingTop: s(20),
    paddingHorizontal: s(10),
  },
  TopBox: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: s(6),
    paddingBottom: s(12),
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    height: s(36),
    borderWidth: 1,
    borderRadius: s(10),
    paddingHorizontal: s(12),
    width: s(200),
  },
  searchBox: {
    flex: 1,
    fontSize: s(14),
    outlineStyle: 'none',
  } as any,
  iconBtn: {
    width: s(34),
    height: s(34),
    borderRadius: s(10),
    justifyContent: 'center',
    alignItems: 'center',
  },
  removeButton: {
    backgroundColor: S.err,
    paddingHorizontal: s(14),
    height: s(34),
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 0,
    borderRadius: s(10),
  },
  deleteModal: {
    borderWidth: 1,
    borderRadius: s(20),
    padding: s(30),
    alignSelf: 'center',
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    width: '35%',
  },
  removeButtonFont: {
    fontSize: s(18),
    fontWeight: 'bold',
    marginHorizontal: s(20),
  },
  headerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: s(5),
    paddingHorizontal: s(14),
    height: s(34),
    borderRadius: s(10),
  },
  registerModal: {
    borderWidth: 1,
    borderRadius: s(20),
    padding: s(30),
    alignSelf: 'center',
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    width: '50%',
    maxWidth: s(500),
  },
  registerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: s(16),
    marginBottom: s(16),
    borderBottomWidth: 2,
  },
  registerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: s(12),
  },
  registerLabel: {
    fontSize: s(15),
    fontWeight: '600',
    width: s(80),
  },
  registerInput: {
    flex: 1,
    height: s(40),
    borderWidth: 1,
    borderRadius: s(10),
    paddingHorizontal: s(12),
    justifyContent: 'center',
  },
  areaGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderWidth: 1,
    borderRadius: s(10),
    padding: s(8),
    gap: s(8),
    maxHeight: s(200),
  },
  areaChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: s(12),
    paddingVertical: s(8),
    borderWidth: 1,
    borderRadius: s(8),
  },
  registerBtn: {
    paddingHorizontal: s(28),
    paddingVertical: s(10),
    borderRadius: s(10),
    justifyContent: 'center',
    alignItems: 'center',
  },
});
